import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  DatePicker,
  Form,
  notification,
  Popconfirm,
  Button as AntButton,
  Input,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  addAssetServiceUse,
  AssetServiceUseResponse,
  deleteAssetServiceUse,
  getAssetServiceUseListByAssocid,
  GetAssetServiceUseListByAssocidParams,
  updateAssetServiceUse,
  updateAssetServiceUseStatus,
} from 'services/assets';
import {
  PlusOutlined,
  CloseOutlined,
  SwapOutlined,
  EditOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { indexBy } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import Button from 'components/Button';
import UpdateButton from 'components/UpdateButtonAction';
import DeleteButton from 'components/DeleteButton';
import InputCurrency from 'components/InputCurrency';
import ThirdPartyInput from 'components/ThirdPartyInput';
import ButtonOperation from 'components/ButtonOperation';
import { toCurrency } from 'utils/utils';
import { ParAssetCatalogServiceResponse } from 'services/assetconfig';

export type AssetServiceUseListProps = {
  record:ParAssetCatalogServiceResponse;
};

const New_useid = -1;

const AssetServiceUseList = (props: AssetServiceUseListProps) => {

  const { associd,assetid,servcode}=props.record;
  const [form] = Form.useForm();
  const fieldName = 'serviceuselist';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'useid',
    addAsFirst: true,
  });

  const initialSearchParams: GetAssetServiceUseListByAssocidParams = {
    associd: associd ?? ('' as any),
  };

  const [searchParams, setSearchParams] =
    useState<GetAssetServiceUseListByAssocidParams>(initialSearchParams);

  const [searchs, setsearchs] = useState(false);

  const [editingKeys, setEditingKeys] = useState<number | null>();
  const isEditable = (useid: number): boolean => editingKeys !== useid;

  const {
    data: uselist,
    refetch,
    isFetching,
  } = useQuery(
    ['getAssetServiceUseListByAssocid', Number(associd)],
    () =>
      getAssetServiceUseListByAssocid({ associd: Number(associd) }).then(
        (res) => {
          if (res.code === '200') {
            return res.list || [];
          } else {
            notification.error({ message: res.message });
            return [];
          }
        }
      ),
    { enabled: !!associd }
  );

  useEffect(() => {
    if (uselist) {
      form?.setFieldsValue({
        [fieldName]: uselist.map((serv) => {
          return {
            ...serv,
            creationdate: serv.creationdate ? moment(serv.creationdate) : '',
            requestdate: serv.requestdate ? moment(serv.requestdate) : '',
            validationdate: serv.validationdate
              ? moment(serv.validationdate)
              : '',
            supplier: {
              tpid: serv.supplierid,
              tpreference: serv.supplierreference,
              tpname: serv.suppliername,
            },
          };
        }),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
    refetch();
  }, [form, uselist, refetch]);

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const servuse: any[] = form.getFieldValue(fieldName);
      const index = indexBy(servuse, 'useid')[record.useid];

      const currentValue = {
        ...servuse[index],
        associd: associd,
        servcode: servcode,
        assetid: assetid,
        creationdate: record.creationdate ? record.creationdate : '',
        requestdate: record.requestdate ? record.requestdate : '',
        validationdate: record.validationdate ? record.validationdate : '',
        suppliername: record.supplier.tpname,
        supplierid: record.supplier.tpid,
        supplierreference: record.supplier.tpreference,
        currcode: localStorage.getItem('currcode') ?? '',
        useridcrearion: Number(localStorage.getItem('userid')),
      };
      currentValue?.useid == New_useid
        ? addAssetServiceUse({
            request: { ...currentValue },
          }).then((response) => {
            if (response.code === '200') {
              refetch();
              notification.success({
                message: response.message,
              });
              setEditingKeys(null);
            }
          })
        : updateAssetServiceUse({
            request: currentValue,
          }).then((response) => {
            if (response.code === '200') {
              refetch();
              notification.success({
                message: response.message,
              });
              setEditingKeys(null);
            }
          });
    },
    [form, refetch]
  );
  const getName = useCallback(
    (name: string) => (record: any) => {
      const serviceuse = form.getFieldValue(fieldName);
      const index = indexBy(serviceuse, 'useid')[record.useid];
      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'creationdate',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('creationdate')(record)} noStyle>
              <DatePicker
                defaultValue={moment(new Date(), 'YYYY-MM-DD')}
                name="creationdate"
              />
            </Form.Item>
          ) : (
            moment(value).format('YYYY-MM-DD')
          );
        },
        width: 140,
      },
      {
        dataIndex: 'requestdate',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('requestdate')(record)} noStyle>
              <DatePicker
                defaultValue={moment(new Date(), 'YYYY-MM-DD')}
                name="requestdate"
              />
            </Form.Item>
          ) : (
            moment(value).format('YYYY-MM-DD')
          );
        },
        width: 140,
      },
      {
        dataIndex: 'validationdate',
        render: (value, record) => {
          return record?.validationdate
            ? moment(record?.validationdate).format('YYYY-MM-DD')
            : '';
        },
        width: 140,
      },
      {
        dataIndex: 'supplier',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('supplier')(record)} noStyle>
              <ThirdPartyInput role="SUPPLIER" />
            </Form.Item>
          ) : (
            record.suppliername
          );
        },
        width: 200,
      },
      {
        dataIndex: 'requestedamount',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('requestedamount')(record)} noStyle>
              <InputCurrency
                currcode={record.currcode ?? localStorage.getItem('currcode')}
              />
            </Form.Item>
          ) : (
            toCurrency(
              value,
              localStorage.getItem('currcode') ?? ''
            )
          );
        },
        width: 140,
      },
      {
        dataIndex: 'acceptedamount',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('acceptedamount')(record)} noStyle>
              <InputCurrency
                currcode={record.currcode ?? localStorage.getItem('currcode')}
              />
            </Form.Item>
          ) : (
            toCurrency(
              value,
              localStorage.getItem('currcode') ?? ''
            )
          );
        },
        width: 140,
      },
      {
        dataIndex: 'useramounttobeinvoiced',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('useramounttobeinvoiced')(record)} noStyle>
              <InputCurrency
                currcode={record.currcode ?? localStorage.getItem('currcode')}
              />
            </Form.Item>
          ) : (
            toCurrency(
              value,
              localStorage.getItem('currcode') ?? ''
            )
          );
        },
        width: 140,
      },
      {
        dataIndex: 'suppliervalidatedamount',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item
              name={getName('suppliervalidatedamount')(record)}
              noStyle
            >
              <InputCurrency
                currcode={record.currcode ?? localStorage.getItem('currcode')}
              />
            </Form.Item>
          ) : (
            toCurrency(
              value,
              localStorage.getItem('currcode') ?? ''
            )
          );
        },
        width: 140,
      },
      {
        dataIndex: 'poorderreference',
        render: (value, record) => {
          return !isEditable(record.useid) ? (
            <Form.Item name={getName('poorderreference')(record)} noStyle>
              <Input />
            </Form.Item>
          ) : (
            record.poorderreference
          );
        },
        width: 140,
      },
      {
        dataIndex: 'requeststatus',
        render: (value, record) => {
          return (
            isEditable(record.useid) && (
              <Tag
                color={
                  record.requeststatus === 'INIT'
                    ? 'lime'
                    : record.requeststatus === 'VALID'
                    ? 'green'
                    : 'red'
                }
              >
                {record.requeststatus}
              </Tag>
            )
          );
        },
        width: 140,
        fixed: 'right',
      },
      {
        key: 'action',
        render: (value, record) => {
          return record.useid === New_useid ? (
            <Row gutter={4}>
              <Col>
                <UpdateButton
                  comcode="save"
                  htmlType="button"
                  size="middle"
                  onClick={(ev) => {
                    ev.preventDefault();
                    onSaveOrEdit(record);
                  }}
                />
              </Col>
              <Col>
                <AntButton
                  danger
                  htmlType="button"
                  icon={
                    <CloseOutlined height="14px" width="14px" color="white" />
                  }
                  onClick={() => {
                    remove(record.useid);
                  }}
                />
              </Col>
            </Row>
          ) : editingKeys === record.useid ? (
            <Row gutter={4}>
              <Col>
                {record.status === 'INIT' ? (
                  <Button
                    comcode="updatestatus"
                    onClick={() =>
                      updateAssetServiceUseStatus({
                        useid: record.useid,
                        status: 'VALID',
                      })
                    }
                    style={{ width: '100%' }}
                    icon={<SwapOutlined />}
                  />
                ) : (
                  <Button
                    comcode="updatestatus"
                    onClick={() =>
                      updateAssetServiceUseStatus({
                        useid: record.useid,
                        status: 'CANCEL',
                      })
                    }
                    style={{ width: '100%' }}
                    icon={<SwapOutlined />}
                  />
                )}
              </Col>
              <Col>
                <UpdateButton
                  comcode="update"
                  htmlType="button"
                  size="middle"
                  onClick={(ev) => {
                    ev.preventDefault();
                    onSaveOrEdit(record);
                  }}
                />
              </Col>
              <Col>
                <AntButton
                  danger
                  htmlType="button"
                  icon={
                    <CloseOutlined height="14px" width="14px" color="white" />
                  }
                  onClick={() => {
                    setEditingKeys(null);
                  }}
                />
              </Col>
            </Row>
          ) : (
            <Row gutter={4}>
              <Col>
                {record.requeststatus === 'INIT' ? (
                  <Button
                    comcode="updatestatus"
                    onClick={() =>
                      updateAssetServiceUseStatus({
                        useid: record.useid,
                        status: 'VALID',
                      })
                    }
                    style={{ width: '100%' }}
                    icon={<SwapOutlined />}
                  />
                ) : (
                  <Button
                    comcode="updatestatus"
                    onClick={() =>
                      updateAssetServiceUseStatus({
                        useid: record.useid,
                        status: 'CANCEL',
                      })
                    }
                    style={{ width: '100%' }}
                    icon={<SwapOutlined />}
                  />
                )}
              </Col>
              <Col>
                <ButtonOperation
                  icon={<EditOutlined />}
                  htmlType="button"
                  size="middle"
                  comcode="edit"
                  onClick={() => {
                    setEditingKeys(record?.useid);
                  }}
                  style={{ height: '20px', width: '14px' }}
                />
              </Col>
              <Col>
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => {
                    deleteAssetServiceUse({
                      useid: record.useid,
                    }).then((res) => {
                      refetch();
                      notification.success({ message: res.message });
                    });
                  }}
                >
                  <DeleteButton onlyIcon />
                </Popconfirm>
              </Col>
            </Row>
          );
        },
        width: 280,
        fixed: 'right',
      },
    ],
    [uselist, form, refetch, getName, editingKeys, onSaveOrEdit, remove]
  );

  const addRow = () => {
    const row: AssetServiceUseResponse = {
      useid: New_useid,
      associd: associd,
      assetid: 0,
      cteid: 0,
      servcode: '',
      currcode: '',
      creationdate: '',
      useridcrearion: Number(localStorage.getItem('userid')),
      requestdate: '',
      requeststatus: 'INIT',
      validationdate: '',
      useridvalidation: 0,
      supplierid: 0,
      suppliername: '',
      supplierreference: '',
      requestedamount: 0,
      requestedqty: 0,
      acceptedamount: 0,
      acceptedqty: 0,
      useramounttobeinvoiced: 0,
      suppliervalidatedamount: 0,
      usecomment: '',
      poorderreference: '',
      externalreference: '',
      siid: 0,
      ciid: 0,
      taskid: 0,
      kilometerscounter: 0,
    };
    add(row);
    setEditingKeys(-1);
  };

  return (
    <DF.Provider pagcode="serviceuselist">
      <Form form={form}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const value: AssetServiceUseResponse[] = getFieldValue(fieldName);
            const isNewItem =
              value?.find(({ useid }) => useid === New_useid) !== undefined;
            return (
              <DataGrid
                rowKey="useid"
                scroll={{ x: 2000, y: 400 }}
                isLoading={isFetching}
                dataSource={value}
                columns={columns}
                onSearch={(values) => {
                  setSearchParams({
                    associd: associd,
                  });
                  setsearchs(true);
                }}
                initialSearchValues={initialSearchParams}
                extraActions={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    comcode="add"
                    disabled={isNewItem}
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

export default AssetServiceUseList;
